import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './auth/AuthContext.js';
import { Layout } from './components/Layout.js';
import { NativePlayerHarness } from './pages/__NativePlayerHarness.js';
import { LoginPage } from './pages/LoginPage.js';
import { LandingPage } from './pages/LandingPage.js';
import { DocsPage } from './pages/DocsPage.js';
import { SetupWizardPage } from './pages/SetupWizardPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { LivePage } from './pages/LivePage.js';
import { AllSessionsPage } from './pages/AllSessionsPage.js';
import { MembersPage } from './pages/MembersPage.js';
import { ProjectsPage } from './pages/ProjectsPage.js';
import { SourcemapsPage } from './pages/SourcemapsPage.js';
import { SessionsPage } from './pages/SessionsPage.js';
import { ReplayPage } from './pages/ReplayPage.js';
import { ApiKeysPage } from './pages/ApiKeysPage.js';
import { BillingPage } from './pages/BillingPage.js';
import { AdminCompaniesPage } from './pages/AdminCompaniesPage.js';
import { AdminOnboardingPage } from './pages/AdminOnboardingPage.js';
import { AdminUsersPage } from './pages/AdminUsersPage.js';
import { AdminPlansPage } from './pages/AdminPlansPage.js';
import { AdminRecaptchaPage } from './pages/AdminRecaptchaPage.js';
import { AdminDemoRequestsPage } from './pages/AdminDemoRequestsPage.js';
import { RecordingRulesPage } from './pages/RecordingRulesPage.js';
import { AdminEmailPage } from './pages/AdminEmailPage.js';
import { VerifyEmailPage } from './pages/VerifyEmailPage.js';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { OAuthConsentPage } from './pages/OAuthConsentPage.js';
import { OAuthCallbackPage } from './pages/OAuthCallbackPage.js';
import { IntegrationsPage } from './pages/IntegrationsPage.js';
import { TicketsPage } from './pages/TicketsPage.js';
import { TicketDetailPage } from './pages/TicketDetailPage.js';
import { FrustrationPage } from './pages/FrustrationPage.js';
import { SecurityPage } from './pages/SecurityPage.js';
import { ErrorsPage } from './pages/ErrorsPage.js';
import { ErrorDetailPage } from './pages/ErrorDetailPage.js';
import { SupportAnalyticsPage } from './pages/SupportAnalyticsPage.js';
import { UserBehaviorPage } from './pages/UserBehaviorPage.js';
import { FunnelPage } from './pages/FunnelPage.js';
import { HeatmapPage } from './pages/HeatmapPage.js';
import { AlertsPage } from './pages/AlertsPage.js';
import { AISummariesPage } from './pages/AISummariesPage.js';
import { AskPage } from './pages/AskPage.js';
import { ApiAccessPage } from './pages/ApiAccessPage.js';
import { ConfigurationPage } from './pages/ConfigurationPage.js';
import { AiSettingsPage } from './pages/AiSettingsPage.js';
import { PublicReplayPage } from './pages/PublicReplayPage.js';
import { HelpPage } from './pages/HelpPage.js';

function Protected({ children }: { children: JSX.Element }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

export function App() {
  return (
    <Routes>
      {/* Marketing home is always at "/"; the app lives under /dashboard. */}
      <Route path="/" element={<LandingPage />} />
      {/* Turkish marketing page. Its own URL so it can be shared and indexed. */}
      <Route path="/tr" element={<LandingPage lang="tr" />} />
      {/* Public documentation, one URL per language so each can be indexed. */}
      <Route path="/docs" element={<DocsPage />} />
      {/* Before /docs/:slug, or the wizard would be read as a document slug. */}
      <Route path="/docs/setup" element={<SetupWizardPage />} />
      <Route path="/tr/docs/setup" element={<SetupWizardPage lang="tr" />} />
      <Route path="/docs/:slug" element={<DocsPage />} />
      <Route path="/tr/docs" element={<DocsPage lang="tr" />} />
      <Route path="/tr/docs/:slug" element={<DocsPage lang="tr" />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<LoginPage />} />
      {/* Public, unauthenticated share viewer. */}
      <Route path="/share/:token" element={<PublicReplayPage />} />
      {/* Email verification link target. Public, since the user may not be signed in. */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/oauth" element={<OAuthCallbackPage />} />
      {/* Where a person approves an assistant's MCP connection. Outside the
          app shell: it is a decision, not a screen to browse away from. */}
      <Route path="/oauth/consent" element={<OAuthConsentPage />} />
      {/* Dev-only: exercises the native player without a recorded session.
          Vite drops this branch from the production bundle. */}
      {import.meta.env.DEV && <Route path="/__native" element={<NativePlayerHarness />} />}
      <Route
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/live" element={<LivePage />} />
        <Route path="/sessions" element={<AllSessionsPage />} />
        <Route path="/tickets" element={<TicketsPage />} />
        <Route path="/tickets/:id" element={<TicketDetailPage />} />
        <Route path="/signals" element={<FrustrationPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/errors" element={<ErrorsPage />} />
        <Route path="/errors/:id" element={<ErrorDetailPage />} />
        <Route path="/support-analytics" element={<SupportAnalyticsPage />} />
        <Route path="/user-behavior" element={<UserBehaviorPage />} />
        <Route path="/analytics/funnel" element={<FunnelPage />} />
        <Route path="/analytics/heatmaps" element={<HeatmapPage />} />
        <Route path="/alerts" element={<AlertsPage />} />
        <Route path="/summaries" element={<AISummariesPage />} />
        <Route path="/ask" element={<AskPage />} />
        <Route path="/configuration" element={<ConfigurationPage />} />
        <Route path="/ai-settings" element={<AiSettingsPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/users" element={<MembersPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/sourcemaps" element={<SourcemapsPage />} />
        <Route path="/projects/:projectId/sessions" element={<SessionsPage />} />
        <Route path="/projects/:projectId/sessions/:sessionId" element={<ReplayPage />} />
        <Route path="/projects/:projectId/api-keys" element={<ApiKeysPage />} />
        <Route path="/api-access" element={<ApiAccessPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/admin" element={<Navigate to="/admin/companies" replace />} />
        <Route path="/admin/onboarding" element={<AdminOnboardingPage />} />
        <Route path="/admin/companies" element={<AdminCompaniesPage />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/plans" element={<AdminPlansPage />} />
        <Route path="/admin/email" element={<AdminEmailPage />} />
        <Route path="/admin/recaptcha" element={<AdminRecaptchaPage />} />
        <Route path="/admin/demo-requests" element={<AdminDemoRequestsPage />} />
        <Route path="/recording" element={<RecordingRulesPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
